import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, MessageCircle, ShoppingCart } from "lucide-react";
import { COMPANY, formatRM } from "@/data/products";

const buildMessage = (items, subtotal) => {
  const lines = [`Hello ${COMPANY.legalName}, I would like to place an order:`, ""];
  items.forEach((i, idx) => {
    lines.push(
      `${idx + 1}. ${i.qty} × ${i.product.name} — ${formatRM(i.product.price)} each = ${formatRM(i.product.price * i.qty)}`
    );
  });
  lines.push("", `Merchandise Subtotal: ${formatRM(subtotal)}`);
  if (items.some((i) => i.product.confirmEdition)) {
    lines.push(
      "",
      "For Lautie LOTTO EDC Toys: please confirm the edition — Titanium+Brass+Mokume (EDC Show Edition) or Titanium+Brass+Zirconium."
    );
  }
  lines.push("", "Please confirm availability, delivery charges and payment method. Thank you!");
  return lines.join("\n");
};

export default function CartDrawer({ open, onClose, cart }) {
  const { items, setQty, remove, subtotal } = cart;
  const empty = items.length === 0;

  const sendOrder = () => {
    if (empty) return;
    const url = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(buildMessage(items, subtotal))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e6e1d8] text-left">
          <SheetTitle className="font-display text-xl font-semibold text-[#10233f]" data-testid="cart-title">
            Your Cart
          </SheetTitle>
          <SheetDescription className="text-sm text-[#5a6b85]">
            Review your items, then send the order to us on WhatsApp.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {empty ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-center" data-testid="cart-empty-state">
              <ShoppingCart className="w-10 h-10 text-[#10233f]/25" strokeWidth={1.2} />
              <p className="text-sm font-semibold text-[#5a6b85]">Your cart is empty.</p>
              <p className="text-xs text-[#8a97ad]">Browse the products and add items to order.</p>
            </div>
          ) : (
            <ul className="space-y-5" data-testid="cart-items">
              {items.map((i) => (
                <li key={i.id} className="flex gap-4 pb-5 border-b border-[#efece6] last:border-0" data-testid={`cart-item-${i.id}`}>
                  <div className="w-20 h-20 rounded-xl bg-[#efece6] overflow-hidden shrink-0 flex items-center justify-center">
                    {i.product.image ? (
                      <img src={i.product.image} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <ShoppingCart className="w-6 h-6 text-[#10233f]/25" strokeWidth={1.2} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#10233f] leading-snug line-clamp-2 mb-1" data-testid={`cart-item-name-${i.id}`}>
                      {i.product.name}
                    </p>
                    <p className="text-xs text-[#8a97ad] mb-2.5" data-testid={`cart-item-unit-${i.id}`}>
                      {formatRM(i.product.price)} each
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 border border-[#e0dad0] rounded-full px-1 py-0.5">
                        <button
                          onClick={() => setQty(i.id, i.qty - 1)}
                          aria-label="Decrease quantity"
                          data-testid={`cart-item-decrease-${i.id}`}
                          className="p-1.5 text-[#10233f] hover:text-[#0d7a72]"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-7 text-center text-sm font-bold" data-testid={`cart-item-qty-${i.id}`}>{i.qty}</span>
                        <button
                          onClick={() => setQty(i.id, i.qty + 1)}
                          aria-label="Increase quantity"
                          data-testid={`cart-item-increase-${i.id}`}
                          className="p-1.5 text-[#10233f] hover:text-[#0d7a72]"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-sm font-bold text-[#10233f]" data-testid={`cart-item-total-${i.id}`}>
                        {formatRM(i.product.price * i.qty)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(i.id)}
                    aria-label="Remove item"
                    data-testid={`cart-item-remove-${i.id}`}
                    className="self-start p-1.5 text-[#8a97ad] hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-[#e6e1d8] px-6 py-5 bg-white">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-[#5a6b85]">Merchandise Subtotal</span>
            <span className="font-display text-xl font-semibold text-[#10233f]" data-testid="cart-subtotal">
              {formatRM(subtotal)}
            </span>
          </div>
          <p className="text-xs text-[#8a97ad] mb-4">
            Availability, delivery charges and payment will be confirmed with you on WhatsApp.
          </p>
          <button
            onClick={sendOrder}
            disabled={empty}
            data-testid="cart-send-whatsapp-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0d7a72] text-sm font-bold text-white hover:bg-[#0a5f59] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MessageCircle className="w-4 h-4" /> Send Order via WhatsApp
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
